import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  styled
} from '@mui/material';

interface PostcardLine {
  year: number | null;
  accountingType: string | null;
  mailCategory: string | null;
  quarter: number | null;
  sendingOe: string;
  receivingOe: string;
  gross: number;
  tare: number;
  net: number;
  netPge: number;
  numberOfItemsPge: number;
  receivedWeightStratum: number | null;
  receivedReceptaclesStratum: number | null;
  numberOfSampledReceptacles: number | null;
  effectiveSamplingRate: number | null;
}

interface PostcardsTableProps {
  data: PostcardLine[];
  direction: string;
}

// Styled components
const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  backgroundColor: '#FFFFFF',
  borderRadius: '6px',
  border: '1px solid #E1E8ED',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
  overflow: 'auto',
  maxHeight: '600px',
}));

const StyledTable = styled(Table)({
  minWidth: 1200,
});

const HeaderCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: '#E7EFF5',
  color: '#063142',
  fontWeight: 600,
  fontSize: '12px',
  padding: '8px 12px',
  border: '1px solid #D0DCE7',
  textAlign: 'center',
  position: 'sticky',
  top: 0,
  zIndex: 1000,
}));

const ComplexHeaderCell = styled(HeaderCell)({
  backgroundColor: '#D1E2ED',
  fontWeight: 700,
  fontSize: '13px',
});

const SubHeaderCell = styled(HeaderCell)({
  backgroundColor: '#DDE8F0',
  fontWeight: 600,
  fontSize: '12px',
});

const DataCell = styled(TableCell)<{ direction?: string; isTotalRow?: boolean }>(({ theme, direction, isTotalRow }) => ({
  fontSize: '12px',
  padding: '6px 12px',
  border: '1px solid #E1E8ED',
  textAlign: 'right',
  backgroundColor: direction === 'Inbound' ? 'rgb(230, 245, 230)' : 'rgb(255, 240, 240)',
  fontWeight: isTotalRow ? 700 : 'normal',
}));

const QuarterCell = styled(DataCell)({
  fontWeight: 600,
  backgroundColor: '#F8F9FA',
  textAlign: 'center',
});

const TotalRowCell = styled(DataCell)<{ direction?: string }>(({ direction }) => ({
  fontWeight: 700,
  backgroundColor: direction === 'Inbound' ? 'rgb(200, 230, 200)' : 'rgb(255, 220, 220)',
  textAlign: 'right',
}));

const TextCell = styled(DataCell)({
  textAlign: 'center',
});

const TotalTextCell = styled(TotalRowCell)({
  textAlign: 'center',
});

function PostcardsTable({ data, direction }: PostcardsTableProps) {
  const formatValue = (value: number | null, decimals: number = 2): string => {
    if (value === null || value === undefined) return '-';
    return decimals === 0 ? Math.round(value).toLocaleString() : value.toFixed(decimals);
  };

  return (
    <StyledTableContainer>
      <StyledTable>
        <TableHead>
          {/* Level 1: Main Headers */}
          <TableRow>
            {/* Quarter column - spans all 3 rows */}
            <HeaderCell rowSpan={3} style={{ minWidth: '80px' }}>
              Quarter
            </HeaderCell>
            
            {/* Office of Exchange - spans 2 columns */}
            <ComplexHeaderCell colSpan={2}>
              Office of Exchange
            </ComplexHeaderCell>
            
            {/* Weight - spans 4 columns */}
            <ComplexHeaderCell colSpan={4}>
              Weight
            </ComplexHeaderCell>
            
            {/* Individual columns - each spans all 3 rows */}
            <HeaderCell rowSpan={3} style={{ minWidth: '120px' }}>
              Number of items<br/>(P+G+E)
            </HeaderCell>
            <HeaderCell rowSpan={3} style={{ minWidth: '120px' }}>
              Received weight<br/>stratum
            </HeaderCell>
            <HeaderCell rowSpan={3} style={{ minWidth: '120px' }}>
              Effective sampling<br/>rate
            </HeaderCell>
            <HeaderCell rowSpan={3} style={{ minWidth: '140px' }}>
              Received receptacles<br/>stratum
            </HeaderCell>
            <HeaderCell rowSpan={3} style={{ minWidth: '140px' }}>
              Number of sampled<br/>receptacles
            </HeaderCell>
          </TableRow>
          
          {/* Level 2: Sub Headers for Office of Exchange and Weight */}
          <TableRow>
            {/* Office of Exchange sub-headers */}
            <SubHeaderCell style={{ minWidth: '100px' }}>
              Sending
            </SubHeaderCell>
            <SubHeaderCell style={{ minWidth: '100px' }}>
              Receiving
            </SubHeaderCell>
            
            {/* Weight sub-headers */}
            <SubHeaderCell style={{ minWidth: '80px' }}>
              Gross
            </SubHeaderCell>
            <SubHeaderCell style={{ minWidth: '80px' }}>
              Tare
            </SubHeaderCell>
            <SubHeaderCell style={{ minWidth: '80px' }}>
              Net
            </SubHeaderCell>
            <SubHeaderCell style={{ minWidth: '100px' }}>
              Net P+G+E
            </SubHeaderCell>
          </TableRow>
        </TableHead>
        
        <TableBody>
          {data.map((row, index) => {
            const isTotalRow = row.quarter === null || row.quarter === undefined;
            const NumberCellComponent = isTotalRow ? TotalRowCell : DataCell;
            const TextCellComponent = isTotalRow ? TotalTextCell : TextCell;
            
            return (
              <TableRow key={index}>
                {/* Quarter */}
                <QuarterCell direction={direction} isTotalRow={isTotalRow}>
                  {row.quarter || ''}
                </QuarterCell>
                
                {/* Office of Exchange - text data, center aligned */}
                <TextCellComponent direction={direction}>
                  {row.sendingOe}
                </TextCellComponent>
                <TextCellComponent direction={direction}>
                  {row.receivingOe}
                </TextCellComponent>
                
                {/* Weight columns - numerical data, right aligned */}
                <NumberCellComponent direction={direction}>
                  {formatValue(row.gross)}
                </NumberCellComponent>
                <NumberCellComponent direction={direction}>
                  {formatValue(row.tare)}
                </NumberCellComponent>
                <NumberCellComponent direction={direction}>
                  {formatValue(row.net)}
                </NumberCellComponent>
                <NumberCellComponent direction={direction}>
                  {formatValue(row.netPge)}
                </NumberCellComponent>
                
                {/* Individual columns - numerical data, right aligned */}
                <NumberCellComponent direction={direction}>
                  {formatValue(row.numberOfItemsPge, 0)}
                </NumberCellComponent>
                <NumberCellComponent direction={direction}>
                  {formatValue(row.receivedWeightStratum)}
                </NumberCellComponent>
                <NumberCellComponent direction={direction}>
                  {formatValue(row.effectiveSamplingRate, 4)}
                </NumberCellComponent>
                <NumberCellComponent direction={direction}>
                  {formatValue(row.receivedReceptaclesStratum, 0)}
                </NumberCellComponent>
                <NumberCellComponent direction={direction}>
                  {formatValue(row.numberOfSampledReceptacles, 0)}
                </NumberCellComponent>
              </TableRow>
            );
          })}
        </TableBody>
      </StyledTable>
    </StyledTableContainer>
  );
}

export default PostcardsTable;
